import { useState } from "react";
import { Mail, ShieldCheck, KeyRound, LogOut } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { ResendCode } from "@/components/ResendCode";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// One-time gate shown after login until the user has a verified email + a chosen PIN.
export default function AccountSetup() {
  const { user, refreshUser, logout } = useAuth();
  const [step, setStep] = useState(user.email_verified ? "pin" : "email");
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendEmailCode = () =>
    run(async () => {
      const { data } = await api.post("/auth/setup/email/start", { email });
      setMaskedEmail(data.email);
      setNotice(
        data.sent
          ? `We've sent a 6-digit code to ${data.email}. It expires in 10 minutes.`
          : `Code generated for ${data.email}, but email delivery is not configured yet — ask your admin.`,
      );
      setStep("email-code");
    });

  const verifyEmail = () =>
    run(async () => {
      await api.post("/auth/setup/email/verify", { code });
      setCode("");
      setNotice("");
      setStep("pin");
    });

  const savePin = () =>
    run(async () => {
      if (pin !== pin2) {
        setError("The two PINs do not match");
        return;
      }
      if (!/^\d{4}$/.test(pin)) {
        setError("PIN must be exactly 4 digits");
        return;
      }
      await api.post("/auth/setup/pin", { pin });
      await refreshUser();
    });

  const dot = (active, done) =>
    `h-2.5 w-2.5 rounded-full ${done ? "bg-brand" : active ? "bg-brand/60" : "bg-slate-200"}`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-tint px-4 py-10">
      <div className="fade-in w-full max-w-md rounded-2xl border border-brand-line bg-white p-8 shadow-sm"
        data-testid="account-setup">
        <BrandMark className="mb-6 h-10" />
        <div className="mb-6 flex items-center gap-2">
          <span className={dot(step === "email" || step === "email-code", user.email_verified)} />
          <span className={dot(step === "pin", false)} />
          <span className="ml-auto text-xs font-medium text-slate-400">One-time setup</span>
        </div>

        {step === "email" && (
          <>
            <div className="mb-2 flex items-center gap-2 text-brand">
              <Mail className="h-5 w-5" />
              <h1 className="font-display text-2xl font-semibold text-brand-ink">Add your email</h1>
            </div>
            <p className="mb-5 text-sm text-slate-500">
              Welcome{user.name ? `, ${user.name.split(" ")[0]}` : ""}. You've signed in with your
              temporary code — this is a <span className="font-medium text-brand-ink">one-time
              step</span>. Add an email (we'll send a code to confirm it), then set a new PIN. From
              now on you'll sign in with your mobile number and that new PIN.
            </p>
            <Label htmlFor="setup-email">Email address</Label>
            <Input
              id="setup-email"
              data-testid="setup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 min-h-[44px]"
              placeholder="you@example.com"
            />
            {error && <p className="mt-3 text-sm font-medium text-rose-600" data-testid="setup-error">{error}</p>}
            <Button
              data-testid="setup-send-email-code"
              className="mt-5 min-h-[44px] w-full"
              disabled={busy || !email}
              onClick={sendEmailCode}
            >
              {busy ? "Sending…" : "Send verification code"}
            </Button>
          </>
        )}

        {step === "email-code" && (
          <>
            <div className="mb-2 flex items-center gap-2 text-brand">
              <ShieldCheck className="h-5 w-5" />
              <h1 className="font-display text-2xl font-semibold text-brand-ink">Verify email</h1>
            </div>
            <p className="mb-5 text-sm text-slate-500" data-testid="setup-notice">
              {notice || `Enter the 6-digit code we sent to ${maskedEmail}.`}
            </p>
            <Label htmlFor="setup-code">Verification code</Label>
            <Input
              id="setup-code"
              data-testid="setup-email-code-input"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1.5 min-h-[44px] tracking-[0.4em]"
              placeholder="••••••"
            />
            {error && <p className="mt-3 text-sm font-medium text-rose-600" data-testid="setup-error">{error}</p>}
            <Button
              data-testid="setup-verify-email"
              className="mt-5 min-h-[44px] w-full"
              disabled={busy || code.length !== 6}
              onClick={verifyEmail}
            >
              {busy ? "Verifying…" : "Verify email"}
            </Button>
            <button
              type="button"
              className="mt-3 w-full text-xs text-slate-500 hover:text-brand"
              onClick={() => setStep("email")}
            >
              Use a different email
            </button>
            <div className="mt-2 text-center">
              <ResendCode
                testid="setup-resend-email-code"
                onResend={async () => {
                  const { data } = await api.post("/auth/setup/email/start", { email });
                  setMaskedEmail(data.email);
                  setNotice(`We've re-sent a code to ${data.email}.`);
                }}
              />
            </div>
          </>
        )}

        {step === "pin" && (
          <>
            <div className="mb-2 flex items-center gap-2 text-brand">
              <KeyRound className="h-5 w-5" />
              <h1 className="font-display text-2xl font-semibold text-brand-ink">Create your PIN</h1>
            </div>
            <p className="mb-5 text-sm text-slate-500">
              Choose a 4-digit PIN. You'll use your mobile number and this PIN to sign in.
            </p>
            <Label htmlFor="setup-pin">New 4-digit PIN</Label>
            <Input
              id="setup-pin"
              data-testid="setup-pin-input"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="mt-1.5 min-h-[44px] tracking-[0.4em]"
              placeholder="••••"
            />
            <Label htmlFor="setup-pin2" className="mt-4 block">Confirm PIN</Label>
            <Input
              id="setup-pin2"
              data-testid="setup-pin-confirm-input"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
              className="mt-1.5 min-h-[44px] tracking-[0.4em]"
              placeholder="••••"
            />
            {error && <p className="mt-3 text-sm font-medium text-rose-600" data-testid="setup-error">{error}</p>}
            <Button
              data-testid="setup-save-pin"
              className="mt-5 min-h-[44px] w-full"
              disabled={busy || pin.length !== 4 || pin2.length !== 4}
              onClick={savePin}
            >
              {busy ? "Saving…" : "Finish setup"}
            </Button>
          </>
        )}

        <button
          type="button"
          data-testid="setup-logout"
          onClick={logout}
          className="mt-6 flex w-full items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-600"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}
